"use client";
import {useContext} from "react";
import {ChevronRight} from "lucide-react";
import {Card, CardContent, CardHeader, CardTitle} from "@/components/shadcn/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/shadcn/ui/accordion";
import { CategoryIcon } from "@/components/templates/contact/category-icon";
import {DictionaryContext} from "@/context/dictionary-context";
import {Dictionary} from "@/types/hygiena-types";

interface MobileContactAccordionProps {
  title: string;
  icon: string;
  categories: { id: number; title: string; description: string; key?: string }[];
  onCategorySelect: (category: string) => void;
  color: string;
  animationDelay?: string;
}

export function MobileContactAccordion({
                                         title,
                                         icon,
                                         categories,
                                         onCategorySelect,
                                         color,
                                         animationDelay = "",
                                       }: MobileContactAccordionProps) {
  const t = useContext<Dictionary>(DictionaryContext);
  const translateTitle = (c: MobileContactAccordionProps["categories"][number]): string =>
    c.key
      ? t?.contact_portal?.[`topic_title_${c.key.replace(/-/g, "_")}`] ?? c.title
      : c.title;
  const translateDesc = (c: MobileContactAccordionProps["categories"][number]): string =>
    c.key
      ? t?.contact_portal?.[`topic_desc_${c.key.replace(/-/g, "_")}`] ?? c.description
      : c.description;
  return (
    <Accordion
      type="single"
      collapsible
      className={`w-full animate-fadeIn ${animationDelay}`}
    >
      <AccordionItem value="item-1" className="border-0">
        <Card className="overflow-hidden hover-lift">
          <CardHeader
            style={{backgroundColor: color}}
            className="text-white p-4"
          >
            <AccordionTrigger className="p-0 hover:no-underline">
              <CardTitle className="flex items-center gap-3 text-left">
                <CategoryIcon icon={icon}/>
                {title}
              </CardTitle>
            </AccordionTrigger>
          </CardHeader>
          <AccordionContent>
            <CardContent className="p-0">
              {categories.length > 0 ? (
                <div className="divide-y">
                  {categories.map((category, index) => (
                    <button
                      key={category.id}
                      className="w-full text-left p-4 hover:bg-gray-50 transition-colors flex items-start gap-3 button-press animate-fadeIn"
                      onClick={() => onCategorySelect(category.title)}
                      style={{animationDelay: `${0.05 * index}s`}}
                    >
                      <div className="flex-1">
                        <h3 className="font-medium text-black">
                          {translateTitle(category)}
                        </h3>
                        <p className="text-sm text-black/75">
                          {translateDesc(category)}
                        </p>
                      </div>
                      <ChevronRight
                        className="h-5 w-5 text-black/30 mt-1 flex-shrink-0 transition-transform duration-200 group-hover:translate-x-1"/>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="p-6 text-center animate-fadeIn">
                  <p className="text-black/75">{t?.contact_portal?.no_topic_available ?? "No topic available"}</p>
                </div>
              )}
            </CardContent>
          </AccordionContent>
        </Card>
      </AccordionItem>
    </Accordion>
  );
}
